// TODO: use centralized component between here and activities-desking

// components
import { Grid } from '@interstate/components/Grid';

import LineItemWithChildren from '../common/LineItemWithChildren';

// constants
import * as constants from '../../constants';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';

// libraries
import type { DealHistory } from '@makemydeal/dr-dash-types';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import DealSummaryItem from '../common/DealSummaryItem';
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';
import { useCtPropertyTaxItems } from '../../offerCompareColumn/hooks/useCtPropertyTaxItems';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';
import { useHeldValue } from '../../hooks/useHeldSelector';

export type MonthlyPaymentProps = {
    deal?: DealHistory;
    hasDelta?: boolean;
};

const MonthlyPayment = ({ deal, hasDelta = false }: MonthlyPaymentProps) => {
    const useDealRootSelector = createDealRootSelector(deal, 'MonthlyPayment');
    // monthly payment
    const monthlyPayment = useHeldValue(useDealRootSelector(offerSelectors.getMonthlyPayment, 'monthlyPayment'), deal);
    // base monthly payment
    const baseMonthlyPayment = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getBaseMonthlyPayment, 'baseMonthlyPayment'),
        deal
    );
    // depreciation
    const depreciation = useHeldValue(useDealRootSelector(offerReduxSelectors.getTotalDepreciation, 'totalDepreciation'), deal);
    // rent charged
    const rentCharge = useHeldValue(useDealRootSelector(offerReduxSelectors.getMonthlyRentCharged, 'monthlyRentCharged'), deal);
    // monthly use tax
    const monthlyUseTax = useHeldValue(useDealRootSelector(offerReduxSelectors.getMonthlyUseTax, 'monthlyUseTax'), deal);
    // CT property tax (additional monthly tax) items, rendered above monthly use tax.
    const ctPropertyTaxItems = useCtPropertyTaxItems(deal);
    // monthly payments total
    const totalBasePayments = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getBaseMonthlyPaymentTotal, 'baseMonthlyPaymentTotal'),
        deal
    );
    // total interest
    const totalInterest = useHeldValue(useDealRootSelector(offerReduxSelectors.getTotalInterest, 'totalInterest'), deal);

    const isEnhancedDealDetailsEnabled = useTrackedSelector(featureToggleSelectors.getEnhancedDealDetails, {
        component: 'MonthlyPayment',
        field: 'enhancedDealDetails'
    });

    if (isEnhancedDealDetailsEnabled) return null;

    return (
        <DealSummaryItem label={constants.MONTHLY_PAYMENT} value={formatDollarsAndCents(monthlyPayment)} hasDelta={hasDelta}>
            <Grid>
                <LineItemWithChildren label={constants.BASE_MO_PAYMENT} value={formatDollarsAndCents(baseMonthlyPayment)} />
                <LineItemWithChildren label={constants.TOTAL_BASE_MO_PAYMENTS} value={formatDollarsAndCents(totalBasePayments)} />
                <LineItemWithChildren label={constants.DEPRECIATION} value={formatDollarsAndCents(depreciation)} />
                <LineItemWithChildren label={constants.RENT_CHARGED} value={formatDollarsAndCents(rentCharge)} />
                <LineItemWithChildren label={constants.TOTAL_INTEREST} value={formatDollarsAndCents(totalInterest)} />
                {ctPropertyTaxItems.map((taxItem) => (
                    <LineItemWithChildren
                        key={taxItem.taxName}
                        label={taxItem.taxDescription}
                        value={formatDollarsAndCents(taxItem.monthlyTax)}
                    />
                ))}
                <LineItemWithChildren label={constants.MO_USE_TAX} value={formatDollarsAndCents(monthlyUseTax)} />
            </Grid>
        </DealSummaryItem>
    );
};

export default MonthlyPayment;
